import type { CSSProperties } from 'react';
import { flexRender, type Column, type Row } from '@tanstack/react-table';
import { TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

/** Sticky positioning for a left-pinned column; empty for non-pinned columns. */
export function pinnedStyle<T>(column: Column<T, unknown>, zIndex: number): CSSProperties {
  if (column.getIsPinned() !== 'left') return {};
  return {
    position: 'sticky',
    left: column.getStart('left'),
    zIndex,
  };
}

export interface DataTableRowProps<T> {
  row: Row<T>;
  /** id of the last left-pinned column — gets the divider border + shadow. */
  lastPinnedLeftId?: string;
  /** z-index for pinned body cells (keep below the header's pinned z). Default 10. */
  pinnedZIndex?: number;
  /** Extra row classes derived from the row (e.g. over-budget highlight). */
  rowClassName?: (row: Row<T>) => string | undefined;
  /**
   * Makes the WHOLE row a click target, as a mouse convenience.
   *
   * ⚠️ This is an ADDITION to a real link/button inside the row, never a replacement for one. A
   * `<tr>` cannot be focused or activated from the keyboard and announces nothing to a screen
   * reader, so a row whose only affordance is this handler is unreachable without a mouse — and
   * middle-click, ⌘-click and "open in new tab" all stop working. Keep the anchor in the cell; this
   * just widens the target from one word to the row.
   */
  onRowClick?: (row: Row<T>) => void;
}

/**
 * Shared presentational row renderer for TanStack tables: emits the `<TableRow>`
 * and its visible `<TableCell>`s, handling left-pinned sticky cells, per-cell
 * `meta.cellClassName`, and selection styling. Both line-item tables converge on
 * this so the row-shell mechanics live in one place (column defs / view-mode logic
 * stay per-table).
 *
 * `'use no memo'` (React Compiler escape hatch — ClickUp 86d39bx22, see CLAUDE.md):
 * this component reads mutable TanStack state (`row.getVisibleCells()`,
 * `getIsSelected()`, `getIsPinned()`) the compiler can't track as a dependency.
 * Its `row` prop keeps a stable reference across a column-visibility toggle, so
 * without the directive the compiler would memoize and skip the re-render, leaving
 * stale/missing cells (e.g. the pricing columns gone after switching to the
 * All/Pricing view while the header updated).
 */
export function DataTableRow<T>({
  row,
  lastPinnedLeftId,
  pinnedZIndex = 10,
  rowClassName,
  onRowClick,
}: DataTableRowProps<T>) {
  'use no memo';
  const isSelected = row.getIsSelected();
  // Sticky cells must be fully opaque so horizontally-scrolled columns can't show
  // through. `bg-background` is the opaque base; the gradient overlay paints the
  // selection/hover tint on top at the same alpha the TableRow uses.
  const stickyBg = isSelected
    ? 'bg-background [background-image:linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))]'
    : 'bg-background group-hover:[background-image:linear-gradient(hsl(var(--muted)/0.5),hsl(var(--muted)/0.5))]';
  return (
    <TableRow
      className={cn('group', isSelected && 'bg-muted/40', onRowClick && 'cursor-pointer', rowClassName?.(row))}
      data-state={isSelected ? 'selected' : undefined}
      onClick={
        onRowClick &&
        ((e) => {
          // Let the row's own controls win. Without this, clicking the document link would fire
          // BOTH the anchor and the row handler, and a ⌘-click would open a new tab and navigate
          // the current one — while a click on a checkbox or a menu button would also navigate away.
          if ((e.target as HTMLElement).closest('a,button,input,select,textarea,[role="checkbox"]')) return;
          // Don't navigate when someone was selecting text in the row (entity names get copied).
          if (window.getSelection()?.toString()) return;
          onRowClick(row);
        })
      }
    >
      {row.getVisibleCells().map((cell) => {
        const meta = cell.column.columnDef.meta as { cellClassName?: string } | undefined;
        const isPinned = cell.column.getIsPinned() === 'left';
        const isLast = isPinned && cell.column.id === lastPinnedLeftId;
        return (
          <TableCell
            key={cell.id}
            style={{ width: cell.column.getSize(), ...pinnedStyle(cell.column, pinnedZIndex) }}
            className={cn(
              meta?.cellClassName,
              isPinned && stickyBg,
              isLast && 'border-r shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]',
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
